import { useEffect } from 'react';
import * as Sentry from '@sentry/browser';
import { getClientDeploymentInfo } from '@/utils/deployment-info';

const SentryInitializer: React.FC = () => {
  useEffect(() => {
    // Initialize Sentry only if DSN is configured and not already initialized
    if (
      import.meta.env.PUBLIC_SENTRY_DSN &&
      !(window as any).__SENTRY_INITIALIZED__ &&
      !Sentry.isInitialized()
    ) {
      // Get deployment information for client-side context
      const deploymentInfo = getClientDeploymentInfo();

      Sentry.init({
        dsn: import.meta.env.PUBLIC_SENTRY_DSN,
        environment: import.meta.env.PUBLIC_SENTRY_ENVIRONMENT || 'production',
        release:
          import.meta.env.PUBLIC_SENTRY_RELEASE || deploymentInfo.deploymentId,

        // Core integrations for global error tracking
        integrations: [
          Sentry.browserTracingIntegration(),
          // Always include replay integration
          Sentry.replayIntegration(),
        ],

        // Performance monitoring
        tracesSampleRate: import.meta.env.PUBLIC_SENTRY_TRACES_SAMPLE_RATE
          ? parseFloat(import.meta.env.PUBLIC_SENTRY_TRACES_SAMPLE_RATE)
          : 0.1,

        // Session Replay configuration - moved to top-level options
        replaysSessionSampleRate: 0.01, // 1% of sessions
        replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

        // Privacy settings
        sendDefaultPii: false,
        maxBreadcrumbs: 50,

        // Only enable in production
        enabled: import.meta.env.PROD,

        // Filter out noise
        beforeSend(event) {
          // Filter out common non-actionable errors
          if (event.exception) {
            const error = event.exception.values?.[0];
            if (
              error?.type === 'ChunkLoadError' ||
              error?.value?.includes('Loading chunk') ||
              error?.value?.includes('script error')
            ) {
              return null;
            }
          }
          return event;
        },
      });

      // Set global context with deployment information
      Sentry.setTag('section', 'global');
      Sentry.setTag('deployment_id', deploymentInfo.deploymentId || 'unknown');
      Sentry.setTag('version_id', deploymentInfo.versionId || 'unknown');
      Sentry.setTag('runtime', 'browser');

      Sentry.setContext('page_info', {
        url: window.location.href,
        pathname: window.location.pathname,
        userAgent: navigator.userAgent,
      });

      Sentry.setContext('deployment_info', {
        deploymentId: deploymentInfo.deploymentId,
        versionId: deploymentInfo.versionId,
        environment: deploymentInfo.environment,
        deploymentTimestamp: deploymentInfo.deploymentTimestamp,
        buildTimestamp: deploymentInfo.buildTimestamp,
      });

      // Mark as globally initialized
      (window as any).__SENTRY_INITIALIZED__ = true;
      (window as any).__SENTRY_REPLAY_INITIALIZED__ = true;

      // Verify replay integration is active
      const client = Sentry.getClient();
      const integrations = client?.getOptions().integrations || [];
      const hasReplay = integrations.some(
        (integration) =>
          integration &&
          typeof integration === 'object' &&
          integration.name === 'Replay',
      );
    }
  }, []);

  // This component renders nothing
  return null;
};

export default SentryInitializer;
